(function initialize365CollectionFacets(root, factory) {
  const api = factory();

  if (typeof module === "object" && module.exports) {
    module.exports = api;
  }

  if (root && root.document) {
    api.start(root);
  }
})(typeof globalThis !== "undefined" ? globalThis : this, function create365CollectionFacets() {
  "use strict";

  const RELEASE_ID = "365nails-collection-purchase-efficiency-v2";
  const MOBILE_BREAKPOINT = 768;
  const DESKTOP_VISIBLE_LIMIT = 6;
  const MOBILE_VISIBLE_LIMIT = 5;
  const OBSERVER_OPTIONS = Object.freeze({
    childList: true,
    subtree: true,
    characterData: true,
  });
  const COLOR_PRIORITY = Object.freeze([
    "pink",
    "white",
    "red",
    "black",
    "blue",
    "multicolor",
    "yellow",
    "silver",
    "green",
    "purple",
    "orange",
    "brown",
    "clear",
  ]);
  const PATTERN_VALUES = new Set(["animal", "floral", "geometric", "tie-dye"]);

  function normalizeLabel(value) {
    return String(value || "").trim().toLowerCase();
  }

  function getVisibleLimit(width) {
    return width < MOBILE_BREAKPOINT ? MOBILE_VISIBLE_LIMIT : DESKTOP_VISIBLE_LIMIT;
  }

  function getVisibleCount(items, width) {
    const selectedCount = items.filter((item) =>
      Boolean(item.querySelector?.('input[type="checkbox"]:checked')),
    ).length;
    return Math.max(getVisibleLimit(width), selectedCount);
  }

  function getDisclosureState(total, visibleCount, expanded) {
    const hasCollapsedValues = total > visibleCount;
    return {
      hidden: !hasCollapsedValues,
      expanded: hasCollapsedValues && Boolean(expanded),
    };
  }

  function classifyValue(label) {
    return PATTERN_VALUES.has(normalizeLabel(label)) ? "pattern" : "color";
  }

  function shouldSeparateTaxonomy(entries) {
    return entries.some((entry) => classifyValue(entry.label) === "pattern");
  }

  function priorityFor(label) {
    const normalized = normalizeLabel(label);
    const priority = COLOR_PRIORITY.indexOf(normalized);
    if (priority >= 0) return priority;
    return PATTERN_VALUES.has(normalized)
      ? COLOR_PRIORITY.length + 100
      : COLOR_PRIORITY.length + 1;
  }

  function sortEntries(entries) {
    return [...entries].sort((left, right) => {
      if (left.checked !== right.checked) return left.checked ? -1 : 1;

      const priorityDelta = priorityFor(left.label) - priorityFor(right.label);
      if (priorityDelta !== 0) return priorityDelta;

      const labelDelta = normalizeLabel(left.label).localeCompare(normalizeLabel(right.label));
      if (labelDelta !== 0) return labelDelta;

      return left.originalIndex - right.originalIndex;
    });
  }

  function facetItems(list) {
    return [...(list?.children || [])].filter((item) =>
      item.matches?.("li") && item.querySelector?.('input[type="checkbox"]'),
    );
  }

  function itemLabel(item) {
    return item.querySelector(".reversed-link__text")?.textContent?.trim()
      || item.querySelector("label")?.textContent?.trim()
      || "";
  }

  function isColorFacet(details) {
    const index = details.getAttribute("data-index") || "";
    const summary = details.querySelector("summary .h6")?.textContent || "";
    return index.endsWith(".custom_color")
      || index.endsWith(".custom.custom_color")
      || normalizeLabel(summary) === "color"
      || normalizeLabel(summary) === "color & pattern";
  }

  function syncButton(details, expanded, total) {
    const button = details.querySelector(".button-show-more");
    if (!button) return;

    const showMore = button.querySelector(".label-show-more");
    const showLess = button.querySelector(".label-show-less");
    if (showMore) {
      if (!showMore.dataset.baseLabel) {
        showMore.dataset.baseLabel = showMore.textContent.trim();
      }
      showMore.textContent = `${showMore.dataset.baseLabel} (${total})`;
      showMore.classList.toggle("hidden", expanded);
    }
    if (showLess) showLess.classList.toggle("hidden", !expanded);

    button.setAttribute("aria-expanded", String(expanded));
    const list = details.querySelector("ul");
    if (list) {
      if (!list.id) {
        const context = (details.getAttribute("data-index") || "facet")
          .replace(/[^a-z0-9_-]+/gi, "-");
        list.id = `365-${context}-values`;
      }
      button.setAttribute("aria-controls", list.id);
    }
  }

  function ensureButton(details, total, limit) {
    let button = details.querySelector(".button-show-more");
    if (button && button.dataset.facetDensityOwned !== "true") {
      const replacement = button.cloneNode(true);
      replacement.dataset.facetDensityOwned = "true";
      button.replaceWith(replacement);
      button = replacement;
    }
    if (button || total <= limit) return button;

    button = details.ownerDocument.createElement("button");
    button.type = "button";
    button.className = "button-show-more btn--link";
    button.dataset.facetDensityCreated = "true";
    button.dataset.facetDensityOwned = "true";

    const showMore = details.ownerDocument.createElement("span");
    showMore.className = "label-show-more label-text";
    showMore.textContent = "Show more";
    const showLess = details.ownerDocument.createElement("span");
    showLess.className = "label-show-less label-text hidden";
    showLess.textContent = "Show less";
    button.append(showMore, showLess);

    details.querySelector(".accordion-details__content")?.append(button);
    return button;
  }

  function syncTaxonomyHeadings(list) {
    for (const heading of list.querySelectorAll(".facet-taxonomy-heading")) {
      let sibling = heading.nextElementSibling;
      let hasVisibleItem = false;
      while (sibling && !sibling.classList.contains("facet-taxonomy-heading")) {
        if (sibling.matches("li") && !sibling.hidden) hasVisibleItem = true;
        sibling = sibling.nextElementSibling;
      }
      heading.hidden = !hasVisibleItem;
    }
  }

  function applyVisibility(details, width, expanded) {
    const list = details.querySelector("ul");
    if (!list) return;

    const items = facetItems(list);
    const limit = getVisibleLimit(width);
    const selectedCount = items.filter((item) =>
      Boolean(item.querySelector('input[type="checkbox"]:checked')),
    ).length;
    const unselectedSlots = Math.max(0, limit - selectedCount);
    let unselectedSeen = 0;
    let visibleCount = 0;

    for (const item of items) {
      const selected = Boolean(item.querySelector('input[type="checkbox"]:checked'));
      const visibleByDefault = selected || unselectedSeen < unselectedSlots;
      if (!selected) unselectedSeen += 1;
      const shouldCollapse = !visibleByDefault;
      const hidden = shouldCollapse && !expanded;
      item.classList.toggle("show-more-item", shouldCollapse);
      item.classList.toggle("hidden", hidden);
      item.hidden = hidden;
      item.toggleAttribute("data-365-facet-collapsed", hidden);
      if (!hidden) visibleCount += 1;
    }

    const button = ensureButton(details, items.length, limit);
    const defaultVisibleCount = Math.min(items.length, Math.max(limit, selectedCount));
    const disclosure = getDisclosureState(items.length, defaultVisibleCount, expanded);
    if (button) button.hidden = disclosure.hidden;
    syncButton(details, disclosure.expanded, items.length);
    syncTaxonomyHeadings(list);
    details.dataset.facetVisibleLimit = String(limit);
    details.dataset.facetVisibleCount = String(visibleCount);
  }

  function ensureTaxonomyHeading(list, kind, before) {
    let heading = list.querySelector(`.facet-taxonomy-heading[data-facet-taxonomy="${kind}"]`);
    if (!heading) {
      heading = list.ownerDocument.createElement("li");
      heading.className = "facet-taxonomy-heading";
      heading.dataset.facetTaxonomy = kind;
      heading.setAttribute("role", "presentation");
      heading.textContent = kind === "pattern" ? "Patterns" : "Colors";
    }
    list.insertBefore(heading, before);
  }

  function separateColorTaxonomy(details) {
    const list = details.querySelector("ul");
    const summary = details.querySelector("summary .h6");
    if (!list) return;

    const entries = facetItems(list).map((item, index) => ({
      item,
      label: itemLabel(item),
      checked: Boolean(item.querySelector('input[type="checkbox"]:checked')),
      originalIndex: Number(item.dataset.originalIndex || index),
    }));

    for (const entry of entries) {
      entry.item.dataset.originalIndex = String(entry.originalIndex);
      entry.item.dataset.facetValueKind = classifyValue(entry.label);
    }

    const colors = sortEntries(entries.filter((entry) => classifyValue(entry.label) === "color"));
    const patterns = sortEntries(entries.filter((entry) => classifyValue(entry.label) === "pattern"));
    for (const heading of list.querySelectorAll(".facet-taxonomy-heading")) heading.remove();
    for (const entry of [...colors, ...patterns]) list.append(entry.item);
    if (shouldSeparateTaxonomy(entries)) {
      if (colors.length) ensureTaxonomyHeading(list, "color", colors[0].item);
      ensureTaxonomyHeading(list, "pattern", patterns[0].item);
    }

    if (summary && patterns.length) summary.textContent = "Color & pattern";
    details.dataset.facetTaxonomySeparated = String(Boolean(patterns.length));
  }

  function bindDisclosure(details, view) {
    const button = details.querySelector(".button-show-more");
    if (!button || button.dataset.facetDensityBound) return;
    button.dataset.facetDensityBound = "true";
    button.addEventListener("click", (event) => {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      const nextExpanded = button.getAttribute("aria-expanded") !== "true";
      applyVisibility(details, view.innerWidth, nextExpanded);
    });
  }

  function enhanceFacet(details, view) {
    if (isColorFacet(details)) separateColorTaxonomy(details);
    const button = ensureButton(
      details,
      facetItems(details.querySelector("ul")).length,
      getVisibleLimit(view.innerWidth),
    );
    const expanded = button?.getAttribute("aria-expanded") === "true";
    applyVisibility(details, view.innerWidth, expanded);
    bindDisclosure(details, view);
    details.dataset.facetDensityEnhanced = RELEASE_ID;
  }

  function bindMobileAccordion(details, view) {
    if (details.dataset.mobileAccordionBound) return;
    details.dataset.mobileAccordionBound = "true";
    details.addEventListener("toggle", () => {
      if (!details.open || view.innerWidth >= MOBILE_BREAKPOINT) return;
      const form = details.closest("form");
      if (!form) return;
      for (const sibling of form.querySelectorAll("details.facet-accordion-details[open]")) {
        if (sibling !== details) sibling.removeAttribute("open");
      }
    });
  }

  function normalizeMobileAccordion(form, view) {
    if (view.innerWidth >= MOBILE_BREAKPOINT) return;
    const open = [...form.querySelectorAll("details.facet-accordion-details[open]")];
    if (open.length <= 1) return;

    const active = open.find((details) => details.querySelector('input[type="checkbox"]:checked'));
    const keep = active || open[0];
    for (const details of open) {
      if (details !== keep) details.removeAttribute("open");
    }
  }

  function enhanceActiveFilters(root) {
    for (const active of root.querySelectorAll(".facets-active")) {
      const filters = [...active.querySelectorAll(".facet-remove")];
      const existing = active.querySelector(".facets-active__intro");
      if (!filters.length) {
        existing?.remove();
        delete active.dataset.collectionFilterSummary;
        continue;
      }

      let intro = existing;
      if (!intro) {
        intro = active.ownerDocument.createElement("div");
        intro.className = "facets-active__intro";
        const title = active.ownerDocument.createElement("strong");
        title.textContent = "Selected filters";
        const count = active.ownerDocument.createElement("span");
        count.className = "facets-active__count";
        intro.append(title, count);
        active.prepend(intro);
      }
      intro.querySelector(".facets-active__count").textContent =
        `${filters.length} ${filters.length === 1 ? "filter" : "filters"} applied`;
      active.dataset.collectionFilterSummary = RELEASE_ID;
    }
  }

  function enhance(root, view) {
    const details = root.querySelectorAll("details.facet-accordion-details");
    const forms = new Set();

    for (const item of details) {
      enhanceFacet(item, view);
      bindMobileAccordion(item, view);
      const form = item.closest("form");
      if (form) forms.add(form);
    }

    for (const form of forms) normalizeMobileAccordion(form, view);
    enhanceActiveFilters(root);
  }

  function createSettlingFacetEnhancer({
    document,
    view,
    Observer = view.MutationObserver,
    enhanceDocument = enhance,
  } = {}) {
    let frame = 0;
    let stopped = false;
    let observer;

    const observe = () => {
      if (!stopped) observer.observe(document.documentElement, OBSERVER_OPTIONS);
    };
    const run = () => {
      frame = 0;
      observer.disconnect();
      try {
        enhanceDocument(document, view);
      } finally {
        observer.takeRecords();
        observe();
      }
    };
    const schedule = () => {
      if (stopped || frame) return;
      frame = view.requestAnimationFrame(run);
    };

    observer = new Observer(schedule);
    observe();

    return {
      schedule,
      disconnect() {
        stopped = true;
        if (frame) view.cancelAnimationFrame?.(frame);
        frame = 0;
        observer.disconnect();
        observer.takeRecords();
      },
    };
  }

  function start(view) {
    const document = view.document;
    if (view.__collectionFacetController) {
      view.__collectionFacetController.schedule();
      return;
    }

    const controller = createSettlingFacetEnhancer({ document, view });
    view.__collectionFacetController = controller;
    const schedule = controller.schedule;

    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", schedule, { once: true });
    } else {
      schedule();
    }

    view.addEventListener("resize", schedule, { passive: true });
    document.addEventListener("shopify:section:load", schedule);
  }

  return {
    COLOR_PRIORITY,
    DESKTOP_VISIBLE_LIMIT,
    MOBILE_BREAKPOINT,
    MOBILE_VISIBLE_LIMIT,
    RELEASE_ID,
    classifyValue,
    createSettlingFacetEnhancer,
    getDisclosureState,
    getVisibleLimit,
    getVisibleCount,
    normalizeLabel,
    priorityFor,
    shouldSeparateTaxonomy,
    sortEntries,
    start,
  };
});
